<script setup>
import {computed} from 'vue';
import {useError, clearError} from 'nuxt/app';
import AppLayout from '~/components/layout/AppLayout.vue';
import {useAuthService} from '~/composables/useAuthService';

// error.vue renders for both 404s (createError statusCode 404) and unexpected
// server/client errors. Keep it on-brand, auth-aware, and helpful — the app chrome
// (nav + footer) stays so the user has somewhere to go, and we never dump the raw
// error object (ugly + leaks internals).
const error = useError();
const {isAuthenticated, user, logout} = useAuthService();

const statusCode = computed(() => error.value?.statusCode ?? 500);
const is404 = computed(() => statusCode.value === 404);
// 403 = the page exists but this account may not see it (a members-only meet
// date opened from an email while signed in as someone else, say). Say so, and
// offer the two things that actually help: the way in, and switching account.
const is403 = computed(() => statusCode.value === 403);
const route = useRoute();
const backTo = computed(() => (is403.value && error.value?.data?.backTo) ? error.value.data : null);
const switchAccount = async () => {
  await logout();
  await clearError({redirect: '/login?authRestricted=true&redirectUrl=' + encodeURIComponent(route.fullPath)});
};
const firstName = computed(() => (isAuthenticated.value && user.value?.firstName) ? user.value.firstName : null);

const eyebrow = computed(() => is404.value ? '404 · Page not found' : (is403.value ? '403 · No access' : `Error ${statusCode.value}`));

const title = computed(() => is404.value
    ? 'That page has wandered off'
    : (is403.value ? 'You don’t have access to this' : 'Something went wrong on our end'));

const subtitle = computed(() => {
  if (is403.value) {
    const who = user.value?.email ? ` You’re signed in as ${user.value.email}.` : '';
    return (error.value?.message || 'This page is only for players in that competition.') + who;
  }
  if (is404.value) {
    return isAuthenticated.value
        ? 'We couldn’t find what you were looking for. It may have moved or the link is out of date. Let’s get you back to the action.'
        : 'We couldn’t find that page. It may have moved, or the link you followed might be out of date.';
  }
  return 'Sorry about that, this one’s on us, and the team has been alerted. Try again in a moment, or get in touch and we’ll put it right.';
});

// The big glyph in the side panel — a lost signpost for 404, an alert for errors.
const glyph = computed(() => is404.value ? 'mdi-map-marker-question-outline' : (is403.value ? 'mdi-lock-outline' : 'mdi-alert-circle-outline'));

// Nuxt error state must be cleared as we navigate away, otherwise the error page sticks.
const goHome = () => clearError({redirect: '/'});
const goTo = (path) => clearError({redirect: path});
const tryAgain = () => {
  if (import.meta.client) window.location.reload();
  else clearError({redirect: '/'});
};
</script>

<template>
  <app-layout>
    <base-page>
      <div class="err-page">
        <v-container class="err-wrap">
          <div class="err-grid">
            <!-- ===== Copy + actions (left, per house style) ===== -->
            <div class="err-copy">
              <div class="err-eyebrow">
                <span class="err-dot"></span>{{ eyebrow }}
              </div>

              <h1 class="err-title">
                <template v-if="firstName">{{ title }}, {{ firstName }}.</template>
                <template v-else>{{ title }}</template>
              </h1>

              <p class="err-sub">{{ subtitle }}</p>

              <!-- Contact callout — only for real errors, where we owe the user a hand. -->
              <a v-if="!is404 && !is403" class="err-contact tl-card" href="mailto:enquiries@tapleagues.com">
                <div class="err-contact-ic"><v-icon size="20">mdi-email-outline</v-icon></div>
                <div class="err-contact-who">
                  <div class="t">enquiries@tapleagues.com</div>
                  <div class="s">Tell us what happened. We usually reply within a working day.</div>
                </div>
                <v-icon size="20" class="err-contact-chev">mdi-chevron-right</v-icon>
              </a>

              <!-- Primary actions -->
              <div v-if="is403" class="err-cta">
                <v-btn v-if="backTo" size="large" color="primary" rounded="pill" class="text-none"
                       prepend-icon="mdi-arrow-left" @click="goTo(backTo.backTo)">
                  {{ backTo.backLabel || 'Go back' }}
                </v-btn>
                <v-btn size="large" :color="backTo ? undefined : 'primary'" :variant="backTo ? 'outlined' : 'flat'"
                       rounded="pill" class="text-none" :class="{'err-cta-ghost': backTo}"
                       prepend-icon="mdi-account-switch-outline" @click="switchAccount">
                  Switch account
                </v-btn>
                <v-btn size="large" variant="outlined" rounded="pill" class="text-none err-cta-ghost"
                       prepend-icon="mdi-home-variant-outline" @click="goHome">
                  {{ isAuthenticated ? 'Back to home' : 'Go to homepage' }}
                </v-btn>
              </div>
              <div v-else class="err-cta">
                <v-btn size="large" color="primary" rounded="pill" class="text-none"
                       prepend-icon="mdi-home-variant-outline" @click="goHome">
                  {{ isAuthenticated ? 'Back to home' : 'Go to homepage' }}
                </v-btn>
                <v-btn v-if="!is404" size="large" variant="outlined" rounded="pill" class="text-none err-cta-ghost"
                       prepend-icon="mdi-refresh" @click="tryAgain">
                  Try again
                </v-btn>
                <v-btn v-else size="large" variant="outlined" rounded="pill" class="text-none err-cta-ghost"
                       prepend-icon="mdi-lifebuoy" @click="goTo('/contact')">
                  Contact us
                </v-btn>
              </div>
            </div>

            <!-- ===== Graphic (right) ===== -->
            <div class="err-art" aria-hidden="true">
              <div class="err-art-panel">
                <span class="err-ring err-ring--1"></span>
                <span class="err-ring err-ring--2"></span>
                <v-icon class="err-art-glyph" :icon="glyph"/>
                <div class="err-art-code">{{ statusCode }}</div>
              </div>
            </div>
          </div>
        </v-container>
      </div>
    </base-page>
  </app-layout>
</template>

<style scoped lang="scss">
.err-page {
  background: rgb(var(--v-theme-light));
  min-height: 70vh;
  display: flex;
  align-items: center;
}

.err-wrap {
  max-width: 1080px;
  padding-top: 48px;
  padding-bottom: 48px;
}

.err-grid {
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 48px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
}

/* ---- copy ---- */
.err-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 14px;
}

.err-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
}

.err-title {
  font-size: clamp(28px, 5vw, 46px);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.02em;
  color: rgb(var(--v-theme-text));
  margin-bottom: 16px;
}

.err-sub {
  font-size: 16px;
  line-height: 1.6;
  color: rgb(var(--v-theme-charcoal));
  max-width: 46ch;
  margin-bottom: 24px;
}

/* ---- contact callout ---- */
.err-contact {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  margin-bottom: 24px;
  text-decoration: none;
  color: inherit;
  max-width: 460px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: rgb(var(--v-theme-primary));
    box-shadow: 0 4px 14px rgba(211, 41, 70, 0.08);
  }
}

.err-contact-ic {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
}

.err-contact-who {
  min-width: 0;

  .t {
    font-size: 14px;
    font-weight: 700;
    color: rgb(var(--v-theme-text));
  }

  .s {
    font-size: 12.5px;
    color: rgb(var(--v-theme-textSubtle));
    line-height: 1.4;
  }
}

.err-contact-chev {
  margin-left: auto;
  color: rgb(var(--v-theme-textSubtle));
}

/* ---- actions ---- */
.err-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 26px;
}

.err-cta-ghost {
  border-color: rgb(var(--v-theme-border));
}

/* ---- graphic ---- */
.err-art {
  display: flex;
  justify-content: center;

  @media (max-width: 900px) {
    order: -1;
  }
}

.err-art-panel {
  position: relative;
  width: min(320px, 74vw);
  aspect-ratio: 1;
  border-radius: 28px;
  background: linear-gradient(155deg, rgb(var(--v-theme-brandTint)) 0%, rgb(var(--v-theme-surface)) 100%);
  border: 1px solid rgb(var(--v-theme-borderLight));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  /* Compact badge on mobile so it doesn't swamp the copy. */
  @media (max-width: 900px) {
    width: 168px;
    border-radius: 22px;
  }
}

.err-ring {
  position: absolute;
  border-radius: 50%;
  border: 1.5px solid rgba(211, 41, 70, 0.14);

  &--1 {
    width: 130%;
    height: 130%;
  }

  &--2 {
    width: 92%;
    height: 92%;
    border-style: dashed;
  }
}

.err-art-glyph {
  font-size: 88px !important;
  color: rgb(var(--v-theme-primary));
  opacity: 0.9;
  z-index: 1;

  @media (max-width: 900px) {
    font-size: 52px !important;
  }
}

.err-art-code {
  z-index: 1;
  margin-top: 6px;
  font-size: 38px;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: rgb(var(--v-theme-text));

  @media (max-width: 900px) {
    font-size: 24px;
    margin-top: 2px;
  }
}
</style>
